
import Vue from "vue";
import { Component, Inject, Prop } from "vue-property-decorator";
import { BuilderActionData, BuilderActions } from "@/types/elements";
import Accordion from "@/components/UI/Accordion.vue";
import { ElementStudioContext } from "@/components/ElementStudio/ElementStudioTabsAndPanels.vue";
import Repeater from "@/components/Reusable/CustomInputs/Repeater/Repeater.vue";
import ShittyTextInput from "@/components/Reusable/CustomInputs/ShittyTextInput.vue";
import defensiveCopy from "@/util/defensive-copy";
import CodeInput from "@/components/Reusable/CustomInputs/CodeInput/CodeInput.vue";
import lodash from "lodash";
import TextWithChevron from "@/components/ElementStudio/TextWithChevron.vue";
import { TAGS_AVAILABLE } from "@/types/tags";

type BuilderActionKeys = keyof BuilderActions;

@Component({
  components: {
    TextWithChevron,
    CodeInput,
    ShittyTextInput,
    Repeater,
    Accordion
  }
})
export default class ElementStudioAction extends Vue {
  @Prop()
  value!: BuilderActions | false;

  @Prop()
  actionKey!: BuilderActionKeys;

  @Prop()
  accordionState!: BuilderActionKeys | "";

  @Prop({ default: true })
  withDependencies!: boolean;

  index = 0;
  actionInfoOpen = false;
  dependencyInfoOpen = false;

  @Inject("context") readonly context!: ElementStudioContext;

  get actionName() {
    return lodash.lowerCase(this.actionKey);
  }

  get tags() {
    return TAGS_AVAILABLE;
  }

  get twigVariables() {
    return this.context.getPathsToAllControls();
  }

  toggleAccordion() {
    this.$emit(
      "toggle",
      this.accordionState === this.actionKey ? "" : this.actionKey
    );
  }

  getValueOrDefault(path: BuilderActionKeys) {
    let newActions = defensiveCopy(this.value);

    if (!newActions) {
      newActions = { [path]: [{ script: "" }] };
    } else if (!newActions[path]) {
      newActions[path] = [{ script: "" }];
    }

    return newActions;
  }

  updateActions(newValues: BuilderActionData[]) {
    const newActions = this.getValueOrDefault(this.actionKey);

    newActions[this.actionKey] = newValues;

    this.context.updateActions(newActions);
  }

  updateAction(newValue: string, path: string) {
    // We know it can't be false here
    const newActions = defensiveCopy(this.value) as BuilderActions;

    lodash.set(newActions, path, newValue);
    this.context.updateActions(newActions);
  }

  updateDependencies(newValue: ({} | string)[], index: number) {
    const newActions = this.getValueOrDefault(this.actionKey);

    let action = newActions[this.actionKey];

    if (!action) {
      action = [];
    }

    // Turn objects into string. It happens when Repeater adds a new item
    action[index].dependencies = newValue.map(script =>
      typeof script === "string" ? script : ""
    );

    newActions[this.actionKey] = action;

    this.context.updateActions(newActions);
  }

  get pathsToAllControls() {
    const controls = this.context.element().controls;
    return [
      controls.designSections.length ? "design" : null,
      controls.contentSections.length ? "content" : null,
      ...this.context.getPathsToAllControls([], [])
    ].filter(Boolean);
  }

  updateDependency(newValue: string, path: string) {
    // We know it can't be false here because it can only be reached after a value being added
    const newActions = defensiveCopy(this.value) as BuilderActions;

    lodash.set(newActions, path, newValue);

    this.context.updateActions(newActions);
  }
}
